import { isRefetchNeededAfterRevealContactDetails } from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/contact-details/utils';
import { ErrorResponse, HttpMethod } from '@aibees/shared/definition';
import { useBaseQuery } from '@aibees/shared/http';
import { useWorkspace } from '@aibees/shared/workspace';
import { UseQueryResult } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { PEOPLE_READ_API_PREFIX } from '../../constants';
import {
  OnReceiveSocketPersonChangesEvent,
  PersonChangesSocketMessage,
  ReadPersonRequest,
  ReadPersonResponse,
} from '../types';
import { useReadPersonChanges } from './use-read-person-changes';
import { useSocketPersonChanges } from './use-socket-person-changes';

export function useReadPerson(id?: string) {
  const workspaceId = useWorkspace()?.id;
  const personChangesApi = useReadPersonChanges(id);

  const api = useBaseQuery<ReadPersonRequest, ReadPersonResponse>(
    [useReadPerson.name, id],
    {
      url: PEOPLE_READ_API_PREFIX.replace(':wid', String(workspaceId)).replace(
        ':id',
        String(id)
      ),
      method: HttpMethod.get,
      headers: { 'x-customer-id': workspaceId },
    },
    {
      enabled: !!id,
    }
  );

  const updatedData = useMemo(
    () =>
      personChangesApi.data && personChangesApi.data.id === api.data?.id
        ? { ...api.data, ...personChangesApi.data }
        : api.data,
    [personChangesApi.data, api]
  );

  // this effect is here because the contact details data havn't included in the personChangesApi data
  // and we need to refetch [just] once after a successful reveal contact progress
  useEffect(() => {
    if (
      isRefetchNeededAfterRevealContactDetails(updatedData) &&
      !api.isFetching
    ) {
      api.refetch();
    }
  }, [updatedData]);

  // refetch the data on related socket events
  const onSocketEvent: OnReceiveSocketPersonChangesEvent = (
    data: PersonChangesSocketMessage
  ) => {
    personChangesApi.debouncedRefetch();
  };

  useSocketPersonChanges(onSocketEvent);

  return useMemo(() => {
    return { ...api, ...{ data: updatedData } } as UseQueryResult<
      ReadPersonResponse,
      ErrorResponse
    >;
  }, [updatedData, api]);
}
